import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { postDataAPI } from "../../helpers/phpbackend_helper";

const AssignServersModal = ({ isOpen, toggle, card }) => {
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [initialSelected, setInitialSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAssigningAll, setIsAssigningAll] = useState(false);
  // state for confirmation popup
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completionDate, setCompletionDate] = useState(null);

  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const defaultDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  }, []);
  useEffect(() => {
    if (isOpen && card?.flash_id) {
      loadData();
      setCompletionDate(defaultDate);
    }
  }, [isOpen, card]);

  const loadData = async () => {
    setLoading(true);

    try {
      // 1️⃣ Load all servers
      const serverRes = await postDataAPI({
        serviceName: "server_data",
        op: "server_coaching_outlet_server_list",
        is_show_all: true,
        load: 0
      });

      // 2️⃣ Load assigned servers for flash card
      const assignedRes = await postDataAPI({
        serviceName: "server_data",
        op: "server_coaching_get_assign_server",
        flash_id: card.flash_id
      });

      if (serverRes.status === "ok") {
        setServers(serverRes.data.server_data || []);
      }

      if (assignedRes.status === "ok") {
        // console.log(assignedRes.data);
        const assignedIds = (assignedRes.data || []).map(Number);
        setSelected(assignedIds);
        setInitialSelected(assignedIds);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleAssignToAllClick = () => {
    const allServerIds = servers.map((s) => Number(s.server_id));
    const hasNewAssignments = allServerIds.some((id) => !initialSelected.includes(id));

    if (hasNewAssignments && !completionDate) {
      alert("Please select a completion date for the assigned servers");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleAssignToAll = async () => {
    setIsAssigningAll(true);

    try {
      const allServerIds = servers.map((s) => Number(s.server_id));

      const payload = {
        serviceName: "server_data",
        op: "create_update_flash_card_servers",
        flash_card_id: card.flash_id,
        server_id: allServerIds,
      };

      if (completionDate) {
        payload.target_complete_date = formatLocalDate(completionDate);
      }

      await postDataAPI(payload);

      setShowConfirmModal(false);
      toggle();
    } finally {
      setIsAssigningAll(false);
    }
  };

  const handleSave = async () => {
    const hasNewAssignments = selected.some((id) => !initialSelected.includes(id));
    if (hasNewAssignments && !completionDate) {
      alert("Please select a completion date for the assigned servers");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        serviceName: "server_data",
        op: "create_update_flash_card_servers",
        flash_card_id: card.flash_id,
        server_id: selected,
      };

      if (completionDate) {
        payload.target_complete_date = formatLocalDate(completionDate);
      }

      await postDataAPI(payload);

      toggle();
    } finally {
      setIsSaving(false);
    }
  };

  const LoadingOverlay = ({ text }) => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255,255,255,0.8)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <Spinner color="primary" />
      <p className="mt-2">{text}</p>
    </div>
  );

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered>
        {(isSaving || isAssigningAll) && (
          <LoadingOverlay
            text={
              isAssigningAll ? "Assigning to all servers..." : "Saving changes..."
            }
          />
        )}
        
        <ModalHeader toggle={toggle}>
          Assign Servers to {card?.item_name}
        </ModalHeader>

        <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading && <p className="text-muted">Loading...</p>}

          {!loading &&
            servers.map((server) => (
              <div
                key={server.server_id}
                className="d-flex align-items-center mb-2"
              >
                <Input
                  type="checkbox"
                  checked={selected.includes(Number(server.server_id))}
                  onChange={() => toggleSelect(Number(server.server_id))}
                  className="me-2"
                />
                <span>{server.name}</span>
              </div>
            ))}
        </ModalBody>

        <ModalFooter className="w-100 justify-content-between">
          <div className="d-flex align-items-center gap-3" style={{ width: "200px" }}>
            <Flatpickr
              className="form-control"
              value={completionDate}
              onChange={(dates) => setCompletionDate(dates[0] || null)}
              options={{
                dateFormat: "Y-m-d",
                minDate: tomorrow,
                altInput: false,
                allowInput: true
              }}
              placeholder="Select Completion Date"
            />
          </div>

          <div>
            <Button outline onClick={toggle} disabled={isSaving} className="me-2">
              Cancel
            </Button>

            <Button
              color="success"
              onClick={handleAssignToAllClick}
              className="me-2"
              disabled={isAssigningAll || isSaving}
            >
              Assign to All
            </Button>

            <Button
              color="primary"
              onClick={handleSave}
              disabled={isSaving || isAssigningAll}
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          Confirm Assign to All
        </ModalHeader>

        <ModalBody>
          Are you sure you want to assign this flash card to{" "}
          <strong>all servers</strong>?
        </ModalBody>

        <ModalFooter>
          <Button
            outline
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>

          <Button
            color="success"
            onClick={handleAssignToAll}
            disabled={isAssigningAll}
          >
            {isAssigningAll ? (
              <>
                <Spinner size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              "Yes, Assign"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AssignServersModal;
